import React, { useEffect } from "react";

import { MdmObjectDto, SortDirection, useGetObjectsPagedLazyQuery, useGetObjectsPagedQuery } from "gql/generated/types";
import { DataTablePFSEvent } from "primereact/datatable";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabPanel, TabView } from "primereact/tabview";
import { Link } from "react-router-dom";
import MapWrapper from "../components/MapWrapper";
import ObjectAndDeviceTableWrapper from "../components/ObjectAndDeviceTableWrapper";
import ObjectMapContent from "../components/ObjectMapContent";
import useProjectContext from "../hooks/useProjectContext";
import { OBJECTS_TABLE_NAME, OBJECT_CONTAINER } from "../utils/const";
import useTableFilter from "../utils/hooks/useTableFilter";
import { TableColumn } from "../utils/types";
import { errorToaster, userTableDetails } from "../utils/utils";

const Objects = () => {
  const {projectId, userSettings} = useProjectContext();
  const tableDetails = userTableDetails(userSettings, OBJECTS_TABLE_NAME);

  const [tableOptions, setTableOptions] = useTableFilter(OBJECTS_TABLE_NAME, {
    sortField: "identifier",
    sortOrder: 1,
  });
  
  const [getNextObjects,{data: objectData, loading }] = useGetObjectsPagedLazyQuery({
    onError: err => errorToaster(err)
  })

  useEffect(() => {
    const localStorageFilters = localStorage.getItem(OBJECTS_TABLE_NAME);
    if(userSettings && !loading) {
      const filters = JSON.parse(localStorageFilters ?? "{}");
      getNextObjects({
        variables: {
          input: {
            projectId,
            take: filters.rows || tableDetails?.rows || 10,
            skip: filters.first || tableOptions.first,
            sort: [
              {
                property: filters.sortField || tableOptions.sortField,
                direction: (filters.sortOrder || tableOptions.sortOrder) === 1 ? SortDirection.Asc : SortDirection.Desc,
              },
            ],
            andFilters: filters.andFilters || tableOptions.andFilters,
            filter: filters.filter || tableOptions.filter,
          }
        },
      })
    }
  }, [userSettings]);

  const objectInfoBtn = (rowData: MdmObjectDto) => (
    <Link to={`objects/${rowData.id}`} title="Object Info">
      {rowData.identifier}
    </Link>
  );

  const objectNameTemplate = (rowData: MdmObjectDto) => (
    <div className="truncate-text text-right lg:text-left" title={rowData.title ?? ""}>
      {rowData.title}
    </div>
  );

  const columns: TableColumn<MdmObjectDto>[] = [
    { field: "identifier", header: "Identifier", body: objectInfoBtn, sortable: true },
    { field: "title", header: "Object Name", body: objectNameTemplate, sortable: true },
    {
      field: "address.fullAddress",
      header: "Address",
      sortable: true,
    },
  ];

  const tableData = (objectData?.mdmObjectsByProjectIdPaged.items || []) as MdmObjectDto[];

  return (
    <TabView panelContainerClassName="p-0 sm:p-4">
      <TabPanel header="Table">
        <ObjectAndDeviceTableWrapper
          columns={columns}
          data={tableData}
          tableName={OBJECTS_TABLE_NAME}
          containerName={OBJECT_CONTAINER}
          loading={loading}
          setTableOptions={setTableOptions}
          tableOptions={tableOptions}
          totalItems={objectData?.mdmObjectsByProjectIdPaged.pageInfo?.total || 0}
          getQueryData={getNextObjects}
        />
      </TabPanel>
      <TabPanel header="Map">
        <ObjectProvider tableOptions={tableOptions} records={objectData?.mdmObjectsByProjectIdPaged.pageInfo?.total || 100}/>
      </TabPanel>
    </TabView>
  );
};

export default Objects;

type ObjectProviderProps = {
  tableOptions: DataTablePFSEvent;
  records: number;
};
const ObjectProvider: React.FC<ObjectProviderProps> = ({tableOptions,records}) => {
  const {projectId} = useProjectContext();

  const { data, loading } = useGetObjectsPagedQuery({
    variables: {
      input: {
        projectId,
        skip: 0,
        take: records,
        andFilters: tableOptions.andFilters,
        filter: tableOptions.filter,
      },
    },
  });

  return (
    <MapWrapper>
      {loading && <ProgressSpinner className='mt-8 w-full' style={{zIndex: 400}}/>}
      <ObjectMapContent objects={(data?.mdmObjectsByProjectIdPaged.items ?? []) as MdmObjectDto[]} />
    </MapWrapper>
  );
}